/*
Copyright 2025 New Vector Ltd.
Copyright 2022 The Matrix.org Foundation C.I.C.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE files in the repository root for full details.
*/

import type { ITimeFormatter } from "../../types/types";
import {Clock} from "./Clock";
import {formatDuration, TimeScope} from "../../../utils/timeFormatting";

export class TimeFormatter implements ITimeFormatter {

    private todayMidnight: Date;
    private relativeDayFormatter: Intl.RelativeTimeFormat;
    private weekdayFormatter: Intl.DateTimeFormat;
    private currentYearFormatter: Intl.DateTimeFormat;
    private otherYearFormatter: Intl.DateTimeFormat;
    private timeFormatter: Intl.DateTimeFormat;

    constructor(private clock: Clock) {
        // don't use the clock time here as the DOM relative formatters don't support setting the reference date anyway
        this.todayMidnight = new Date();
        this.todayMidnight.setHours(0, 0, 0, 0);
        this.relativeDayFormatter = new Intl.RelativeTimeFormat(undefined, {numeric: "auto"});
        this.weekdayFormatter = new Intl.DateTimeFormat(undefined, {weekday: 'long'});
        this.currentYearFormatter = new Intl.DateTimeFormat(undefined, {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
        });
        this.otherYearFormatter = new Intl.DateTimeFormat(undefined, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        this.timeFormatter = new Intl.DateTimeFormat(undefined, {hour: "numeric", minute: "2-digit"});
    }
    
    formatTime(date: Date): string {
        return this.timeFormatter.format(date);
    }

    formatMachineReadableDate(date: Date): string {
        return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;    
    }

    formatRelativeDate(date: Date): string {
        let daysDiff = Math.floor((date.getTime() - this.todayMidnight.getTime()) / TimeScope.Day);
        if (daysDiff >= -1 && daysDiff <= 1) {
            // Tomorrow, Today, Yesterday
            return capitalizeFirstLetter(this.relativeDayFormatter.format(daysDiff, "day"));
        } else if (daysDiff > -7 && daysDiff < 0) {
            // Wednesday
            return this.weekdayFormatter.format(date);
        } else if (this.todayMidnight.getFullYear() === date.getFullYear()) {
            // Friday, November 6
            return this.currentYearFormatter.format(date);
        } else {
            // Friday, November 5, 2021
            return this.otherYearFormatter.format(date);
        }
    }

    formatDuration(milliseconds: number): string {
        return formatDuration(milliseconds);
    }
}

function capitalizeFirstLetter(str: string) {
    return str.slice(0, 1).toLocaleUpperCase() + str.slice(1);
}
